"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import Head from "next/head";
import Footer from "@/components/Footer";
import CallToAction, { WhiteSpace } from "@/components/EssentialComponents";
/* ----------------------------------
   Composant : Carte d'un service
---------------------------------- */
type ServiceProps = {
  title: string;
  summary: string;
  details: string[];
  // price?: string;
  image: string;
};

const ServiceCard: React.FC<ServiceProps> = ({
  title,
  summary,
  details,
  // price,
  image,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <>
      {/* Carte cliquable */}
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setShowDetails(true)}
        className="
          bg-AWS_black border border-black rounded-xl shadow-md
          overflow-hidden cursor-pointer
          flex flex-col
          w-full
          h-[320px]
        "
      >
        {/* Image ~180 px */}
        <div className="overflow-hidden h-[220px]">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Contenu textuel */}
        <div className="p-4 flex flex-col flex-grow justify-between">
          <div>
            <h3 className="text-lg font-arimo font-semibold text-white">{title}</h3>
            <p className="text-gray-400 text-sm mt-2">{summary}</p>
          </div>

          {/* 
          // Si besoin, vous pouvez réafficher le prix :
          {price && (
            <p className="text-blue-500 font-bold mt-2">{price}</p>
          )} 
          */}
        </div>
      </motion.div>

      {/* Modal des détails */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-AWS_black bg-opacity-75 backdrop-blur-md"
          >
            <div className="relative flex flex-col md:flex-row gap-8 w-full max-w-4xl bg-AWS_black p-8 rounded-2xl text-white shadow-2xl">
              <button
                onClick={() => setShowDetails(false)}
                className="absolute top-4 right-4 text-AWS_grey text-3xl hover:text-white"
              >
                ×
              </button>

              {/* Image dans la modale */}
              <div className="w-full md:w-[430px] flex items-center justify-center">
                <img
                  src={image}
                  alt={title}
                  className="w-[430px] h-[384px] rounded-xl object-cover shadow-lg"
                />
              </div>

              {/* Texte + liste de détails */}
              <div className="flex-1 flex flex-col justify-center">
                <h2 className="text-4xl font-arimo mb-6 text-white text-left">
                  {title}
                </h2>
                <ul className="text-gray-300 text-lg leading-relaxed space-y-3">
                  {details.map((detail, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <CheckCircleIcon className="h-6 w-6 text-green-500" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
                {/*
                {price && (
                  <p className="text-blue-500 font-bold mt-6 text-3xl text-left">
                    {price}
                  </p>
                )}
                */}
                <a
                  href="/contact"
                  className="mt-8 inline-block bg-AWS_blue hover:bg-AWS_darkBlue text-white font-arimo font-bold py-3 px-6 rounded-xl text-center"
                >
                  Demander un devis
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

/* ----------------------------------
   Données des catégories de services
---------------------------------- */
const categories = [
  {
    title: "Création de Sites Web Vitrine",
    services: [
      {
        title: "Site Vitrine Essentiel",
        summary: "Site de 3 pages avec design personnalisé.",
        details: [
          "Page d'accueil, Services/Produits et Contact",
          "Design responsive et personnalisé",
          "Sécurisation basique (SSL, firewall)",
          "Optimisation SEO de base",
          "Formulaire de contact intuitif",
        ],
        // price: "1 200 €",
        image: "services/essentiel.png",
      },
      {
        title: "Site Vitrine Professionnel",
        summary: "Site de 5 pages avec design personnalisé.",
        details: [
          "Conception de 5 pages",
          "Architecture de contenu optimisée",
          "Design sur mesure et responsive",
          "Sécurisation avancée",
          "Optimisation SEO renforcée",
        ],
        // price: "2 500 €",
        image: "services/professionnel.png",
      },
      {
        title: "Site Vitrine Premium",
        summary: "Site de 8 pages et fonctionnalités avancées.",
        details: [
          "Création de 8 pages ou plus",
          "Personnalisation poussée de l'UX/UI",
          "Design sur mesure",
          "Responsive et optimisé SEO",
          "Support technique renforcé",
        ],
        // price: "4 000 €",
        image: "services/premium.png",
      },
    ],
  },
  {
    title: "Design Graphique & Identité Visuelle",
    services: [
      {
        title: "Logo Professionnel",
        summary: "Création d'un logo unique et mémorable.",
        details: [
          "Deux propositions créatives",
          "Jusqu'à trois révisions incluses",
          "Fichiers en haute résolution (PNG, JPEG)",
          "Fichiers sources (AI, PSD) et déclinaisons de couleurs",
        ],
        // price: "500 €",
        image: "services/logo-pro.png",
      },
      {
        title: "Carte de Visite",
        summary: "Design recto-verso professionnel.",
        details: [
          "Design impactant et sur mesure",
          "Adapté à votre charte graphique",
          "Fichiers haute résolution prêts pour impression",
        ],
        // price: "150 €",
        image: "services/visite.png",
      },
      {
        title: "Flyer Recto",
        summary: "Flyer attractif pour promouvoir votre activité.",
        details: [
          "Design moderne et percutant",
          "Création d’un flyer d’une page",
          "Idéal pour des campagnes ciblées",
        ],
        // price: "200 €",
        image: "services/recto.png",
      },
      {
        title: "Dépliant 3 Volets",
        summary: "Dépliant 6 pages pour une présentation détaillée.",
        details: [
          "Design complet de 6 pages",
          "Présentation détaillée de vos services",
          "Communication structurée et visuelle",
        ],
        // price: "350 €",
        image: "services/depliant.png",
      },
    ],
  },
  // {
  //   title: "Packs Combinés",
  //   services: [
  //     {
  //       ...
  //     },
  //   ],
  // },
  {
    title: "Services Additionnels",
    services: [
      {
        title: "Maintenance Mensuelle",
        summary: "Mises à jour et support technique.",
        details: [
          "Mises à jour régulières",
          "Sauvegardes automatiques",
          "Support technique réactif",
        ],
        // price: "100 €/mois",
        image: "services/maintenance.png",
      },
      {
        title: "Référencement (SEO) Avancé",
        summary: "Optimisation de votre visibilité en ligne.",
        details: [
          "Audit complet de votre site",
          "Optimisation des balises et du contenu",
          "Suivi des performances et ajustements réguliers",
        ],
        // price: "800 €",
        image: "services/seo.png",
      },
      {
        title: "Formation à la Gestion de Contenu",
        summary: "Formation personnalisée pour gérer votre site.",
        details: [
          "Session de formation personnalisée",
          "Conseils pratiques pour la gestion de votre site",
          "Accompagnement pour une mise à jour autonome",
        ],
        // price: "300 €",
        image: "services/formation.png",
      },
    ],
  },
];

/* ----------------------------------
   Page principale : ServicesPage
---------------------------------- */
export default function ServicesPage() {
  return (
    <div className="bg-AWS_black  text-white pt-40">
      <Head>
        <title>Création de Sites Web, SEO, Design | Alex Web Solutions</title>
        <meta
          name="description"
          content="Création de sites internet professionnels, refonte, SEO, logos et maintenance. Des solutions web accessibles et sur-mesure pour tous."
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.alexwebsolutions.com/services" />
      </Head>

      {/* En-tête */}
      <div className="py-6 px-4">
        <h1 className="text-5xl font-arimo text-center mb-4">
          Nos Offres & Services Détaillés
        </h1>
        <p className="text-xl text-gray-400 text-center mb-8 max-w-3xl mx-auto">
          Découvrez en détail l’ensemble de nos prestations pour booster votre
          présence digitale. Chaque service est conçu pour répondre à vos besoins
          spécifiques.
        </p>
      </div>

      {/* Conteneur principal */}
      <div className="max-w-6xl mx-auto px-4 pb-10">
        {categories.map((category, index) => (
          <div key={index} className="mb-10">
            <h2 className="text-2xl font-arimo mb-4 text-center">
              {category.title}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {category.services.map((service, idx) => (
                <ServiceCard key={idx} {...service} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <WhiteSpace />
      <CallToAction />
      <Footer />
    </div>
  );
}